import React, { useState, useContext, useEffect, useImperativeHandle, useRef } from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTagInput from "components/SuiTagInput";

import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import NumbersIcon from "@mui/icons-material/Numbers";

import toast from "react-hot-toast";

import CategoryContext from "context/category-context";

import api from "services/api";
import moment from "moment";

import { sizeOptions, seasonOptions } from "utils/data";
import { DatePicker, Tooltip } from "antd";

const Color = require("color");

const CreateColorModal = React.forwardRef(
  (
    {
      visible,
      handleClose,
      handleOk,
      isEdit = false,
      color = {},
      handleRefresh = () => {},
      handleAfterOk = () => {},
      refreshDelay = 0,
      readOnly = false,
    },
    ref
  ) => {
    const [uploading, setUploading] = useState(false);
    const [newColor, setNewColor] = useState("#000000");
    const [confirmVisible, setConfirmVisible] = useState(false);

    if (ref) {
      useImperativeHandle(
        ref,
        () => ({
          newColor,
        }),
        [setNewColor]
      );
    }

    const {
      register,
      handleSubmit,
      trigger,
      setValue,
      getValues,
      reset,
      watch,
      control,
      formState: { errors },
    } = useForm();

    useEffect(() => {
      if (isEdit) {
        setNewColor(color?.hex);

        let colorObj = undefined;
        try {
          colorObj = Color(color?.hex);
        } catch (err) {}

        let seasonOpt = seasonOptions.find((x) => x.value == color?.season) || {};
        if (color.season && !seasonOpt.value) {
          seasonOpt = { label: color.season, value: color.season };
        }

        let resetPayload = {
          name: color?.name,
          season: seasonOpt?.value ? seasonOpt : undefined,
          hex: color?.hex,
          rgb_r: colorObj ? colorObj.object().r : undefined,
          rgb_g: colorObj ? colorObj.object().g : undefined,
          rgb_b: colorObj ? colorObj.object().b : undefined,
          note: color?.note,
          year: color?.metadata?.year ? moment(color?.metadata?.year) : null,
        };

        reset(resetPayload);
      } else {
        reset({
          hex: newColor,
          rgb_r: 0,
          rgb_g: 0,
          rgb_b: 0,
          year: moment(),
        });
      }
    }, [isEdit]);

    useEffect(() => {
      const subscription = watch((value, { name, type }) => {
        if (name == "rgb_r" && type == "change") {
          const rgb_g = getValues("rgb_g");
          const rgb_b = getValues("rgb_b");
          try {
            const colorObj = Color({ r: value.rgb_r, g: rgb_g, b: rgb_b });
            setNewColor(colorObj?.hex());
          } catch (err) {}
        } else if (name == "rgb_g" && type == "change") {
          const rgb_r = getValues("rgb_r");
          const rgb_b = getValues("rgb_b");
          try {
            const colorObj = Color({ r: rgb_r, g: value.rgb_g, b: rgb_b });
            setNewColor(colorObj?.hex());
          } catch (err) {}
        } else if (name == "rgb_b" && type == "change") {
          const rgb_r = getValues("rgb_r");
          const rgb_g = getValues("rgb_g");
          try {
            const colorObj = Color({ r: rgb_r, g: rgb_g, b: value.rgb_b });
            setNewColor(colorObj?.hex());
          } catch (err) {}
        }
      });
      return () => subscription.unsubscribe();
    }, [watch]);

    const handleCreateColor = async (data) => {
      try {
        Color(newColor);
      } catch (err) {
        return toast.error(`Invalid color!`);
      }
      try {
        let payload = {
          name: data["name"],
          season: data["season"]?.value || null,
          hex: newColor,
          note: data["note"],
          metadata: {
            ...color?.metadata,
            year: data["year"] ? moment(data["year"]).format("YYYY") : null,
          },
        };

        if (isEdit) {
          if (!color.id) {
            return toast.error("Can't find the color!");
          }
          //check if hex code is different, then allow user to double confirm
          if (newColor == color.hex) {
            const newCreatedColor = await api.palette.update(color.id, payload);
            toast.success(`${data["name"]} has been saved successfully!`);
            handleClose();
            handleAfterOk(newCreatedColor);
          } else {
            setConfirmVisible(true);
          }
        } else {
          const newCreatedColor = await api.palette.create(payload);
          toast.success(`${data["name"]} has been saved successfully!`);
          handleClose();
          handleAfterOk(newCreatedColor);
        }

        setTimeout(() => {
          handleRefresh();
        }, refreshDelay);
      } catch (err) {
        console.log(err);
        toast.error(`Color could not be saved!`);
        setUploading(false);
      }
    };

    const handleColorChange = (e) => {
      const hex = e.target.value.toUpperCase();
      setNewColor(hex);
      try {
        //update form value
        const colorObj = Color(hex);
        setValue("hex", colorObj.hex());
        setValue("rgb_r", colorObj.object().r);
        setValue("rgb_g", colorObj.object().g);
        setValue("rgb_b", colorObj.object().b);
      } catch (err) {}
    };

    const handleCloseConfirm = () => {
      setConfirmVisible(false);
    };

    const handleConfirmEdit = async () => {
      let data = getValues();
      let payload = {
        name: data["name"],
        season: data["season"]?.value || null,
        hex: newColor,
        note: data["note"],
        metadata: {
          ...color?.metadata,
          year: data["year"] ? moment(data["year"]).format("YYYY") : null,
        },
      };
      try {
        const newCreatedColor = await api.palette.update(color.id, payload);
        toast.success(`${data["name"]} has been saved successfully!`);
        handleClose();
        handleCloseConfirm();
        handleAfterOk(newCreatedColor);
        setTimeout(() => {
          handleRefresh();
        }, refreshDelay);
      } catch (err) {
        console.log(err);
        toast.error(`Color could not be saved!`);
      }
    };

    const handleConfirmDuplicate = async () => {
      let data = getValues();
      let payload = {
        name: "Duplicate of " + data["name"],
        season: data["season"]?.value || null,
        hex: newColor,
        note: data["note"],
        metadata: {
          ...color?.metadata,
          year: data["year"] ? moment(data["year"]).format("YYYY") : null,
        },
      };
      try {
        const newCreatedColor = await api.palette.create(payload);
        toast.success(`${data["name"]} has been saved successfully!`);
        handleClose();
        handleCloseConfirm();
        handleAfterOk(newCreatedColor);
        setTimeout(() => {
          handleRefresh();
        }, refreshDelay);
      } catch (err) {
        console.log(err);
        toast.error(`Color could not be saved!`);
      }
    };

    return (
      <Dialog
        open={visible}
        onClose={(e, reason) => {
          if (reason != "backdropClick") {
            handleClose();
          }
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
        fullWidth={true}
      >
        <Backdrop sx={{ color: "info", zIndex: (theme) => 99999 }} open={uploading}>
          <CircularProgress color="info" sx={{ mr: 1 }} size={32} />
          <SuiTypography variant="h6" fontWeight="medium" color="info">
            Uploading supporting files...
          </SuiTypography>
        </Backdrop>
        <DialogTitle id="alert-dialog-title">
          {readOnly ? color?.name : isEdit ? "Edit Color" : "Create Color"}

          <IconButton
            aria-label="close"
            onClick={() => handleClose()}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form
          onSubmit={handleSubmit(handleOk ? handleOk : handleCreateColor)}
          style={{ overflow: "auto", maxHeight: "600px" }}
        >
          <fieldset disabled={readOnly}>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} md={5}>
                    <SuiBox>
                      <SuiBox ml={0.5}>
                        <SuiTypography
                          variant="button"
                          fontWeight="bold"
                          textTransform="capitalize"
                        >
                          Color Swatch
                        </SuiTypography>
                      </SuiBox>
                      <SuiBox px={4}>
                        <input
                          type="color"
                          value={newColor}
                          onChange={handleColorChange}
                          style={{
                            width: "100%",
                            height: 240,
                            cursor: "pointer",
                          }}
                        />
                      </SuiBox>
                    </SuiBox>
                  </Grid>

                  <Grid item xs={12} sm={12} md={7}>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={12}>
                        <SuiBox
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                          height="100%"
                        >
                          <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                            <SuiTypography
                              component="label"
                              variant="caption"
                              fontWeight="bold"
                              required
                              textTransform="capitalize"
                            >
                              Color Name
                            </SuiTypography>
                          </SuiBox>
                          <SuiInput
                            placeholder="Color Name"
                            required
                            inputProps={{ maxLength: 200 }}
                            {...register("name", { required: "A color name is required!" })}
                          />
                        </SuiBox>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <SuiBox
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                          height="100%"
                        >
                          <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                            <SuiTypography
                              component="label"
                              variant="caption"
                              fontWeight="bold"
                              textTransform="capitalize"
                            >
                              Color HEX
                            </SuiTypography>
                          </SuiBox>
                          <Controller
                            name="hex"
                            control={control}
                            render={({ field }) => (
                              <SuiInput
                                {...field}
                                value={newColor}
                                onChange={(e) => {
                                  let value = e.target.value.toUpperCase();
                                  value = value.replace(/[^\dA-F]/g, "");
                                  if (!value.startsWith("#")) {
                                    value = "#" + value;
                                  }
                                  try {
                                    const colorObj = Color(value);
                                    setValue("rgb_r", colorObj.object().r);
                                    setValue("rgb_g", colorObj.object().g);
                                    setValue("rgb_b", colorObj.object().b);
                                  } catch (err) {}
                                  setNewColor(value);
                                }}
                                inputProps={{ maxLength: 7 }}
                              />
                            )}
                          />
                        </SuiBox>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <SuiBox
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                          height="100%"
                        >
                          <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                            <SuiTypography
                              component="label"
                              variant="caption"
                              fontWeight="bold"
                              textTransform="capitalize"
                            >
                              Color RGB
                            </SuiTypography>
                          </SuiBox>
                          <SuiBox display="flex" justifyContent="flex-start" gap={1}>
                            <SuiInput
                              placeholder="R"
                              {...register("rgb_r")}
                              inputProps={{ type: "number", max: 255, min: 0, step: 1 }}
                            />
                            <SuiInput
                              placeholder="G"
                              {...register("rgb_g")}
                              inputProps={{ type: "number", max: 255, min: 0, step: 1 }}
                            />
                            <SuiInput
                              placeholder="B"
                              {...register("rgb_b")}
                              inputProps={{ type: "number", max: 255, min: 0, step: 1 }}
                            />
                          </SuiBox>
                        </SuiBox>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <SuiBox
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                          height="100%"
                        >
                          <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                            <SuiTypography
                              component="label"
                              variant="caption"
                              fontWeight="bold"
                              textTransform="capitalize"
                            >
                              Year
                            </SuiTypography>
                          </SuiBox>
                          <Controller
                            name="year"
                            control={control}
                            render={({ field }) => (
                              <DatePicker
                                {...field}
                                picker="year"
                                placeholder="Year"
                                style={{
                                  height: 40,
                                  width: "100%",
                                  borderRadius: "8px",
                                  border: "1px solid #d2d6da",
                                }}
                                disabled={readOnly}
                                popupStyle={{
                                  maxHeight: 400,

                                  overflow: "auto",
                                  zIndex: 9999,
                                }}
                              />
                            )}
                          />
                        </SuiBox>
                      </Grid>{" "}
                      <Grid item xs={12} sm={6}>
                        <SuiBox
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                          height="100%"
                        >
                          <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                            <SuiTypography
                              component="label"
                              variant="caption"
                              fontWeight="bold"
                              textTransform="capitalize"
                            >
                              Season
                            </SuiTypography>
                          </SuiBox>
                          <Controller
                            name="season"
                            control={control}
                            render={({ field }) => (
                              <SuiSelect
                                {...field}
                                options={seasonOptions}
                                placeholder="Season"
                                backspaceRemovesValue
                                isClearable
                                isCreatable
                                isDisabled={readOnly}
                              />
                            )}
                          />
                        </SuiBox>
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <SuiBox
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                          height="100%"
                        >
                          <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                            <SuiTypography
                              component="label"
                              variant="caption"
                              fontWeight="bold"
                              textTransform="capitalize"
                            >
                              Description
                            </SuiTypography>
                          </SuiBox>
                          <SuiInput
                            placeholder="Add a new event to your Calendar"
                            rows={4}
                            multiline={true}
                            fullWidth={true}
                            inputProps={{ maxLength: 2000 }}
                            {...register(`note`)}
                          />
                        </SuiBox>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <SuiBox
                  pt={3}
                  px={3}
                  display={readOnly ? "none" : "flex"}
                  justifyContent="end"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  flexDirection={{ xs: "column", sm: "row" }}
                >
                  <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                    <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                      <SuiButton
                        variant="gradient"
                        size="medium"
                        onClick={handleClose}
                        color="light"
                      >
                        Cancel
                      </SuiButton>
                    </SuiBox>
                  </SuiBox>
                  <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                    <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                      <SuiButton size="medium" variant="gradient" color="dark" type="submit">
                        {isEdit ? "Save" : "Create"}
                      </SuiButton>
                    </SuiBox>
                  </SuiBox>
                </SuiBox>
              </DialogContentText>
            </DialogContent>
          </fieldset>
        </form>
        {confirmVisible ? (
          <Dialog
            open={confirmVisible}
            onClose={handleCloseConfirm}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">{"Confirm Edit"}</DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                The related color in style will be updated if you click "Edit".
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <SuiBox display="flex" justifyContent="space-between" width="100%">
                <SuiButton onClick={handleConfirmDuplicate} color="dark" variant="gradient">
                  Duplicate
                </SuiButton>
                <SuiBox display="flex" gap={1}>
                  <SuiButton onClick={handleCloseConfirm}>Cancel</SuiButton>

                  <SuiButton onClick={handleConfirmEdit} autoFocus color="dark" variant="gradient">
                    Edit
                  </SuiButton>
                </SuiBox>
              </SuiBox>
            </DialogActions>
          </Dialog>
        ) : null}
      </Dialog>
    );
  }
);

export default CreateColorModal;
